import { useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon, type SenoIconName } from '../components/SenoIcon';
import { get } from '../lib/api';
import type { Project, User } from '../types';
import { BrainPage } from './BrainPage';
import { DocumentPage } from './DocumentPage';
import { FilesPage } from './FilesPage';
import { FinalPage } from './FinalPage';
import { OverviewPage } from './OverviewPage';
import { RevisionPage } from './RevisionPage';

type ProjectTab = 'overview' | 'brain' | 'files' | 'document' | 'revision' | 'final';

const mainNav: Array<{ tab: ProjectTab; label: string; icon: SenoIconName }> = [
  { tab: 'overview', label: 'Skripsi Saya', icon: 'skripsi' },
  { tab: 'files', label: 'Bahan', icon: 'bahan' },
  { tab: 'revision', label: 'Revisi', icon: 'revisi' },
  { tab: 'final', label: 'Finalisasi', icon: 'finalisasi' }
];

export function ProjectShell({
  projectId,
  tab,
  user,
  navigate
}: {
  projectId: string;
  tab: ProjectTab;
  user: User;
  navigate: (path: string) => void;
}) {
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    get<{ project: Project }>(`/api/projects/${projectId}`)
      .then((data) => setProject(data.project))
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Proyek tidak dapat dibuka.')
      );
  }, [projectId]);
  if (error)
    return (
      <div className="page-error">
        <ErrorNotice message={error} />
        <button className="button button-secondary" onClick={() => navigate('/app')}>
          Kembali ke proyek
        </button>
      </div>
    );
  if (!project) return <Loading />;
  const base = `/app/project/${projectId}`;
  const effectiveMain = tab === 'brain' || tab === 'document' ? 'overview' : tab;
  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <button className="brand-button" onClick={() => navigate('/app')}>
          <BrandMark />
        </button>
        <nav className="primary-nav" aria-label="Navigasi utama">
          {mainNav.map((item) => (
            <button
              key={item.tab}
              className={effectiveMain === item.tab ? 'active' : ''}
              onClick={() => navigate(`${base}/${item.tab}`)}
            >
              <SenoIcon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-context">
          <span>Ruang proyek</span>
          <button
            className={tab === 'brain' ? 'active' : ''}
            onClick={() => navigate(`${base}/brain`)}
          >
            <SenoIcon name="validasi" /> Project Brain
          </button>
          <button
            className={tab === 'files' ? 'active' : ''}
            onClick={() => navigate(`${base}/files`)}
          >
            <SenoIcon name="pedoman" /> Pedoman & Berkas
          </button>
          <button
            className={tab === 'document' ? 'active' : ''}
            onClick={() => navigate(`${base}/document`)}
          >
            <SenoIcon name="revisi" /> Dokumen
          </button>
        </div>
        <button className="sidebar-profile" onClick={() => navigate('/app')}>
          <span className="avatar">{user.displayName.charAt(0)}</span>
          <span>
            <strong>{user.displayName}</strong>
            <small>Kembali ke semua proyek</small>
          </span>
        </button>
      </aside>
      <div className="app-main">
        <header className="mobile-top">
          <BrandMark compact />
          <div>
            <strong>{project.name}</strong>
            <span>{project.type === 'thesis' ? 'Skripsi' : 'Jurnal'}</span>
          </div>
        </header>
        {tab === 'overview' ? <OverviewPage project={project} navigate={navigate} /> : null}
        {tab === 'brain' ? <BrainPage projectId={projectId} /> : null}
        {tab === 'files' ? <FilesPage projectId={projectId} /> : null}
        {tab === 'document' ? <DocumentPage projectId={projectId} /> : null}
        {tab === 'revision' ? <RevisionPage projectId={projectId} /> : null}
        {tab === 'final' ? <FinalPage projectId={projectId} /> : null}
      </div>
      <nav className="bottom-nav" aria-label="Navigasi mobile">
        {mainNav.map((item) => (
          <button
            key={item.tab}
            className={effectiveMain === item.tab ? 'active' : ''}
            onClick={() => navigate(`${base}/${item.tab}`)}
          >
            <SenoIcon name={item.icon} />
            <span>{item.label.replace('Skripsi ', '')}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
